import { action, computed, flow, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { defaultUser, defaultUsersFilters, defaultUserValidation } from '../constants/users';
import rolesStore from './rolesStore';
import { SCREENS } from 'src/constants/screens';
import { USER_ROLES } from 'src/constants/user';
import { userStore } from 'src/mobx/userStore';
import client from 'src/services/client';
import { IBranch } from 'src/types/branches';
import { LoadingStatus, paginationCast, TBooleanInNumber, TPagination } from 'src/types/main';
import { IUserV4, IV4UserRole } from 'src/types/user';
import { booleanNumberReverse } from 'src/utils/helpers';
import branchesStore from 'src/view/screens/BranchesManagementScreen/screens/BranchesScreen/store/branchesStore';
import {
  IDefaultUserFieldValidation,
  IUsersFilters,
  IUserValidation,
} from 'src/view/screens/SettingsScreen/screens/General/ManageUsers/screens/AllUsers/types/users';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { localeStore } from '~/mobx/localesStore';

class UsersStore {
  constructor() {
    makeObservable(this);
  }

  getUsersAbortController = new AbortController();
  getUserAbortController = new AbortController();
  usersGetTimeoutId: NodeJS.Timeout | null = setTimeout(() => {
    return;
  }, 0);
  @persist @observable public perPage: number = 10;
  @observable public usersLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public userLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public userCreateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public userUpdateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @persist @observable users: IUserV4[] = [];
  @persist @observable public pagination: TPagination = { currentPage: 0, lastPage: 0, totalRows: 0, perPage: 0 };
  @observable user: IUserV4 | null = null;
  @observable userBranch?: IBranch;
  @observable edited: boolean = false;
  @observable filters: IUsersFilters = defaultUsersFilters;
  @observable totalBranches: number = 0;
  @observable validation: IUserValidation = defaultUserValidation;

  @action setFilters = (filters: Partial<IUsersFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this._getUsersWithDelay(1);
  };

  @computed get rolesOptions(): IPickerOption[] {
    return [{ label: localeStore.t('select_all'), value: 0 }, ...rolesStore.selectRolesOptions];
  }

  @computed get selectedRoles(): IPickerOption[] {
    const roles = rolesStore.selectRolesOptions.filter((role) => this.filters.roles?.includes(role.value as number));
    if (this.rolesOptions.length === this.filters.roles?.length) {
      return [{ label: localeStore.t('select_all'), value: 0 }, ...roles];
    }
    return roles;
  }

  @action selectAllRoles = () => {
    this.filters.roles = this.rolesOptions.map((role) => role.value);
  };

  private getRoles = ({
    isAllRolesSelected,
    isAllBranchesValueSelected,
    allRoles,
    options,
  }: {
    isAllRolesSelected: boolean;
    isAllBranchesValueSelected: boolean;
    allRoles: number[];
    options: IPickerOption[];
  }) => {
    if (isAllBranchesValueSelected) {
      return allRoles;
    } else {
      if (isAllBranchesValueSelected) {
        return [0, ...options.map((option) => option.value)];
      }
      return options.map((option) => option.value);
    }
  };

  @action changeRoles = (options: IPickerOption[]) => {
    const allRoles = [{ label: localeStore.t('select_all'), value: 0 }, ...rolesStore.selectRolesOptions].map((role) => role.value);
    let roles: number[] | undefined = undefined;
    const optionsLength = options.filter((option) => option.value !== 0).length;
    const isAllRolesSelected = optionsLength === allRoles.length - 1;
    const isAllBranchesValueSelected = options.some((options) => options.value === 0);

    const isAllRolesWasSelected = this.filters.roles?.some((role) => role === 0);
    if (isAllRolesWasSelected) {
      if (isAllRolesSelected) {
        roles = isAllBranchesValueSelected ? allRoles : undefined;
      } else {
        roles = options.filter(({ value }) => value !== 0).map(({ value }) => value);
      }
    } else {
      roles = this.getRoles({ allRoles, isAllBranchesValueSelected, isAllRolesSelected, options });
    }

    this.filters = { ...this.filters, roles };
    this._getUsersWithDelay(1);
  };

  @action fetchAdditionalData = async () => {
    await branchesStore.fetchBranchesList();
    this.totalBranches = branchesStore.pagination.total;
  };

  private _getUsers = flow(function* (this: UsersStore, page: number): any {
    this.getUsersAbortController.abort();
    this.getUsersAbortController = getAbortController();

    try {
      this.usersLoadingStatus = LoadingStatus.Loading;
      const params: any = {
        page,
        search: this.filters.search || '',
        status: this.filters.status || undefined,
        role: this.filters.roles?.filter((role) => role !== 0) || undefined,
        per_page: this.filters.perPage || undefined,
        sort_field: this.filters.sortField || undefined,
        sort_order: this.filters.sortDir || undefined,
        is_delete: 0,
      };

      const response = yield client.get('v4/users', {
        params,
        signal: this.getUsersAbortController.signal,
      });

      this.users = this.sortUsersList(response.data);
      this.pagination = paginationCast(response);
      this.usersLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.usersLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.usersLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'UsersStore _getUsers', showModal: true });
    }
  }).bind(this);
  @action getUsers = async () => {
    if (this.usersLoadingStatus === LoadingStatus.Init) {
      await this._getUsersWithNoDelay(1);
    } else {
      await this._getUsersWithNoDelay(this.pagination.currentPage);
    }
  };

  @action private _getUsersWithNoDelay = async (pageNumber: number = 1) => {
    if (this.usersGetTimeoutId) {
      clearTimeout(this.usersGetTimeoutId);
    }
    await this._getUsers(pageNumber);
  };

  @action private _getUsersWithDelay = (pageNumber: number = 1) => {
    if (this.usersGetTimeoutId) {
      clearTimeout(this.usersGetTimeoutId);
    }
    this.usersGetTimeoutId = setTimeout(() => {
      this._getUsers(pageNumber);
    }, 1000);
  };

  @action public changePage = (pageNumber: number) => {
    this._getUsersWithNoDelay(pageNumber);
  };

  // need to put authenticated user to the first place in users array
  private sortUsersList(users: IUserV4[]) {
    return users.sort((first, second) => {
      if (first.id === userStore.user.id) {
        return -1;
      }
      if (second.id === userStore.user.id) {
        return 1;
      }
      return 0;
    });
  }

  @action getUser = async (id: string | number) => {
    this.getUserAbortController.abort();
    this.getUserAbortController = getAbortController();

    try {
      this.userLoadingStatus = LoadingStatus.Loading;

      const user: IUserV4 = await client.get(`v4/users/${id}`, {
        headers: { 'Accept-Language': 'en,ar' },
        signal: this.getUserAbortController.signal,
      });

      this.setUser(user && user.id ? user : undefined);
      this.userLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.userLoadingStatus = LoadingStatus.Success;
        return;
      }

      logger.error({ error, place: 'UsersStore getUser', showModal: true });
      this.userLoadingStatus = LoadingStatus.Error;
    }
  };

  @action setUser = (user?: IUserV4) => {
    this.user = user ? user : defaultUser;
    this.user.name = this.user.first_name;
    this.userLoadingStatus = LoadingStatus.Success;
    this.userUpdateLoadingStatus = LoadingStatus.Init;
    this.userCreateLoadingStatus = LoadingStatus.Init;
    this.validation = defaultUserValidation;
  };

  @action updateStatus = async (userId: number, status: TBooleanInNumber) => {
    try {
      this.users = this.users.map((user) => (user.id === userId ? { ...user, status } : user));
      await client.post(`v4/users/${userId}/change-status`, { status });
    } catch (error: any) {
      this.users = this.users.map((user) => (user.id === userId ? { ...user, status: booleanNumberReverse(user.status) } : user));
      logger.error({ error, place: 'UsersStore updateStatus', showModal: true });
    }
  };

  @action deleteUser = async (userId: number) => {
    try {
      this.usersLoadingStatus = LoadingStatus.Refreshing;
      await client.delete(`v4/users/${userId}`);
      this._getUsersWithNoDelay(this.pagination.currentPage);
    } catch (error: any) {
      this.usersLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'UsersStore deleteUser', showModal: true });
    }
  };

  @action updateUser = async () => {
    if (!this.user?.id) return;
    const data = this.composeForApi();
    if (!data) return;

    try {
      this.userLoadingStatus = LoadingStatus.Refreshing;
      this.userUpdateLoadingStatus = LoadingStatus.Refreshing;

      await client.put(`v4/users/${this.user.id}`, { ...data });

      this.users.map((user) => (user.id === this.user?.id ? { ...this.user } : user));
      this.userLoadingStatus = LoadingStatus.Success;
      this.userUpdateLoadingStatus = LoadingStatus.Success;
      this.edited = false;
    } catch (error: any) {
      this.userLoadingStatus = LoadingStatus.Error;
      this.userUpdateLoadingStatus = LoadingStatus.Error;

      if (this.isInvalidDataResponse(error, data)) return;

      logger.error({ error, place: 'UsersStore updateUser', showModal: true });
    }
  };

  @action createUser = async () => {
    if (!this.user) return;
    const data = this.composeForApi();
    if (!data) return;

    try {
      this.userCreateLoadingStatus = LoadingStatus.Loading;
      this.userLoadingStatus = LoadingStatus.Refreshing;

      const response = await client.post(`v4/users`, { ...data });

      this.users.map((user) => (user.id === this.user?.id ? { ...this.user } : user));
      this.user.id = response.id;
      this.userCreateLoadingStatus = LoadingStatus.Success;
      this.userLoadingStatus = LoadingStatus.Success;
      this.edited = false;
    } catch (error: any) {
      this.userCreateLoadingStatus = LoadingStatus.Error;
      this.userLoadingStatus = LoadingStatus.Error;

      if (this.isInvalidDataResponse(error, data)) return;
      logger.error({ error, place: 'UsersStore createUser', showModal: true });
    }
  };

  private composeForApi() {
    if (!this.user) return;
    return {
      role: this.user.roles[0] ? this.user.roles[0].id : undefined,
      status: this.user.status,
      branches: (this.user.branches || []).map((branch) => branch.id),
      franchises: (this.user.franchises || []).map((franchisee) => franchisee.id),
      name: (this.user?.name || '').trim(),
      username: this.user.username,
      email: this.user.email,
      password: this.user?.password || undefined, // min:6, regex:/^.*(?=.{3,})(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[\d\x])(?=.*[@$!%*#?&]).*$/
      mobile: this.user?.mobile ? '+' + String(Number(this.user.mobile)) : undefined,
    };
  }

  private isInvalidDataResponse(error: any, requestData: any) {
    this.validation = defaultUserValidation;
    for (const [key, value] of Object.entries(error.response.data)) {
      if (!Object.keys(requestData).includes(key)) {
        continue;
      }
      const _errorBag = value as string[];
      this.addValidationError(key, {
        state: false,
        errorText: _errorBag[0] || undefined,
      });
    }
    return this.hasUserValidationErrors();
  }

  @action changeUserName = (name: string) => {
    if (!this.user) return;
    this.clearValidationKey('name');

    this.edited = true;
    const value = name
      .replace(/\d/g, '') // Remove digits
      .replace(/  +/g, ' ') // Replace multi spaces with one space
      .replace(/^ $/, '') // Remove space if field was empty (first symbol is space)
      .trimLeft(); // Remove space before name

    this.user.name = value;
  };

  private addValidationError(key: string, error: IDefaultUserFieldValidation) {
    if (!Object.keys(this.validation).includes(key)) {
      return;
    }
    this.validation = {
      ...this.validation,
      [key]: error,
    };
  }

  private clearValidationKey(key: string) {
    let errorKey = key;
    if (key === 'roles') {
      errorKey = 'role';
    }
    if (!Object.keys(this.validation).includes(errorKey)) {
      return;
    }
    this.validation = {
      ...this.validation,
      [errorKey]: {
        state: true,
        errorText: undefined,
      } as Partial<IUserValidation>,
    };
  }

  private hasUserValidationErrors = (): boolean => {
    return Object.values(this.validation).filter((value: IDefaultUserFieldValidation) => !value.state).length > 0;
  };

  @action changeUserHandler = (value: Partial<IUserV4>) => {
    if (!this.user) return;
    const key = Object.keys(value)[0];
    if (key === 'roles' && !this.userHasRole(USER_ROLES.FRANCHISEE, value.roles)) {
      this.changeUserHandler({ franchises: [] });
    }
    this.clearValidationKey(key);
    this.user = { ...this.user, ...value };
    this.edited = true;
  };

  @computed get isUserRestaurantManager(): boolean {
    return this.userHasRole(USER_ROLES.RESTAURANT_MANAGER);
  }

  @computed get isUserFranchise(): boolean {
    return this.userHasRole(USER_ROLES.FRANCHISEE);
  }

  private userHasRole(roleName: string, roles: IV4UserRole[] = []): boolean {
    const _roles = roles.length === 0 ? this.user?.roles || [] : roles;
    const allRoles = rolesStore.roles;
    const userRoles = allRoles.filter((_role) => _roles.findIndex((userRole) => userRole.id === _role.id) !== -1);
    return userRoles.find((role) => role.name === roleName) !== undefined;
  }

  @action unMount = () => {
    this.user = null;
    this.userBranch = undefined;
    this.edited = false;

    if (!window.location.pathname.includes(SCREENS.SETTINGS_USERS())) this.filters = defaultUsersFilters;
  };
}

const usersStore = new UsersStore();

export default usersStore;
